import { useEffect } from "react";
import { createPortal } from "react-dom";
import { PrimaryButton } from "@/components/Buttons/PrimaryButton";
import { SecondaryButton } from "@/components/Buttons/SecondaryButton";
import { useTranslations } from "next-intl";

type Props = {
  /** The guest's current login username — shown verbatim so the user sees
   *  exactly the value that stays embedded in emails / login links. */
  username: string;
  onConfirm: () => void;
  onCancel: () => void;
};

/**
 * Warning shown when a guest's first/last name is changed but the login
 * `username` is left untouched. The username is a stable login credential that
 * is NOT derived from the name: it stays fixed on purpose (changing it would
 * break login links the guest already received) and it surfaces in guest
 * communication — the `{{username}}` email variable and every login link
 * (`/login?credentials=base64(username:code)`). This dialog only warns; it does
 * not change the username.
 */
export function UsernameUnchangedDialog({
  username,
  onConfirm,
  onCancel,
}: Props) {
  const t = useTranslations("guestDetails");

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return createPortal(
    <div
      className="fixed inset-0 z-10001 flex items-center justify-center bg-black/40 px-4"
      onClick={onCancel}
      role="presentation"
    >
      <div
        className="w-full max-w-100 surface-primary rounded-2xl shadow-xl p-6 flex flex-col gap-4"
        onClick={(e) => e.stopPropagation()}
        role="alertdialog"
        aria-modal="true"
        aria-label={t("usernameUnchangedTitle")}
      >
        <div className="flex flex-col gap-1">
          <p className="h3">{t("usernameUnchangedTitle")}</p>
          <p className="body-3-regular font-secondary">
            {t("usernameUnchangedBody", { username })}
          </p>
        </div>
        <div className="flex items-center justify-end gap-2">
          <SecondaryButton
            size="small"
            width="w-max"
            text={t("usernameUnchangedCancel")}
            onClick={onCancel}
          />
          <PrimaryButton
            size="small"
            width="w-max"
            text={t("usernameUnchangedConfirm")}
            onClick={onConfirm}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
}
